import { sanitizeAndTrimInput } from "utils/helpers/string";

export const priceFormatter = (number) => {
  let formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(number);
};

export const priceFormatterWithoutCurrency = (number) => {
  let formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(number);
};

export const validateEmail = (email) => {
  return String(sanitizeAndTrimInput(email) ?? "")
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

export const preventMinus = (e) => {
  if (e.code === "Minus") {
    e.preventDefault();
  }
};

export const preventPasteNegative = (e) => {
  const clipboardData = e.clipboardData || window.clipboardData;
  const pastedData = parseFloat(clipboardData.getData("text"));

  if (pastedData < 0) {
    e.preventDefault();
  }
};

export const validatePassword = (password) => {
  return /^(?=.*\d)(?=.*[a-zA-Z]).{8,100}$/.test(password);
};

export const handleKeyDown = (event) => {
  // Allow only arrow up and arrow down key events
  if (
    event.key === "ArrowUp" ||
    event.key === "ArrowDown" ||
    event.key === "-"
  ) {
    event.preventDefault();

    // Get the current value of the input
    const currentValue = parseFloat(event.target.value) || 0;

    // Calculate the new value based on the arrow key pressed
    let newValue;
    if (event.key === "ArrowUp") {
      newValue = currentValue + 1;
    } else if (event.key === "ArrowDown") {
      newValue = currentValue - 1;
    }

    // Prevent negative values
    if (newValue < 0) {
      newValue = 0;
    }

    // Update the input value
    event.target.value = newValue;
  }
};

export const validateValueGreaterThanZero = (rule, value) => {
  if (!value) {
    return Promise.reject("This field needs to be filled");
  }

  const parsedValue = parseInt(value, 10); // Convert the value to an integer

  if (isNaN(parsedValue) || parsedValue <= 0) {
    return Promise.reject("Value must be a positive number");
  }

  return Promise.resolve();
};

export const validateSpaces = (rule, value) => {
  if (!value) {
    return Promise.reject("This field needs to be filled");
  }

  if (!value.trim()) {
    return Promise.reject("Value must be some meaningful");
  }

  return Promise.resolve();
};

export const avoidMinus = (event) => {
  // Check if the pressed key is "-"
  if (event.key === "-") {
    event.preventDefault();
  }
};

export const validatePostalCodeLength = (value) => {
  // Remove leading and trailing spaces
  value = value.trim();

  if (value.length !== 6 && value.length !== 7) {
    return false;
  }

  const postalCodePattern = /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/;

  return postalCodePattern.test(value);
};

export const generateId = () => {
  return Math.floor(Math.random() * 10000) + 10000000;
};

export const formatPhoneNumber = (phone) => {
  if (phone && phone?.startsWith("+1")) {
    phone = phone?.slice(2);

    if (phone?.length == 10) {
      const areaCode = phone.slice(0, 3);
      const firstThree = phone.slice(3, 6);
      const lastFour = phone.slice(6, 10);
      return `(${areaCode}) ${firstThree}-${lastFour}`;
    }
  }

  return phone;
};

export const formatInputPhoneNumber = (value, { isDeleting }) => {
  if (!value) return "";

  if (isDeleting && (value == "+" || value == "+1" || value == "+1 "))
    return value;

  if (value == "+" || value == "+1" || value == "+1 ") return value;

  const hasPlusOne = value.startsWith("+1");

  let digits = value.startsWith("+1")
    ? value.slice(2)
    : value.startsWith("+")
    ? value.slice(1)
    : value;

  digits = digits.replace(/\D/g, "").slice(0, 10);

  if (digits.length < 4) return hasPlusOne ? `+1 ${digits}` : digits;
  if (digits.length < 7)
    return hasPlusOne
      ? `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`
      : `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

  return hasPlusOne
    ? `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    : `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const isValidPhoneNumber = (phone) => {
  if (!phone) return false;

  // Remove all non-digit characters
  let digits = phone.replace(/\D/g, "");

  // If starts with '1' and length > 10, strip the leading 1 (country code)
  if (digits.startsWith("1") && digits.length > 10) {
    digits = digits.slice(1);
  }

  // Validate: exactly 10 digits
  return digits.length == 10;
};
